import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/app/store/hooks';
import { setTokens, clearTokens } from '@/features/auth';
import { initAuth } from '@/shared/auth/initAuth';
import { registerDevice } from '@/shared/auth/registerDevice';
import { mergeCarts } from '@/shared/auth/mergeCarts';
import { linkBotUser } from '@/shared/auth/linkBotUser';
import { validateSession } from '@/shared/auth/validateSession';
import { extractEsiaError } from '@/shared/auth/extractEsiaCode';
import {
  isAuthPayloadConsumed,
  markAuthPayloadConsumed,
} from '@/shared/auth/consumedAuthPayload';
import { getAppMode } from '@/shared/utils';
import { errorTypeOf, track, type ErrorType } from '@/shared/analytics';
import { api } from '@/shared/api';
import { store } from '@/app/store';
import type { AuthResult } from '@/shared/types';

function trackAuthFailed(errorType: ErrorType) {
  track('auth_failed', {
    method: 'esia',
    result: 'error',
    error_type: errorType,
  });
}

export function RootRedirect() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isRunning = useRef(false);

  useEffect(() => {
    if (isRunning.current) return;
    isRunning.current = true;

    const hasStoredSession = () => {
      const { accessToken, refreshToken } = store.getState().login;
      return Boolean(accessToken || refreshToken);
    };

    const resumeSession = async (): Promise<boolean> => {
      if (!hasStoredSession()) return false;

      const check = await validateSession(dispatch);

      if (check === 'unauthorized') {
        dispatch(clearTokens());
        return false;
      }

      navigate('/home', { replace: true });
      return true;
    };

    const isMax = getAppMode() === 'max';

    // ── MAX-режим ──────────────────────────────────────────────────
    if (isMax) {
      const webApp = window.WebApp;
      const startParam = webApp?.initDataUnsafe?.start_param;
      const maxUserId = webApp?.initDataUnsafe?.user?.id?.toString();

      const runMax = async () => {
        let authResult: AuthResult | null = null;
        let exchangeFailed = false;

        try {
          authResult = await initAuth(startParam, dispatch);
        } catch (err) {
          console.error('[initAuth]', err);
          exchangeFailed = true;
          trackAuthFailed(errorTypeOf(err));
        }

        if (authResult) {
          dispatch(
            setTokens({
              accessToken: authResult.accessToken,
              refreshToken: authResult.refreshToken,
            })
          );

          await registerDevice(dispatch).catch((err) =>
            console.error('[registerDevice]', err)
          );

          await mergeCarts(dispatch).catch((err) =>
            console.error('[mergeCarts]', err)
          );

          if (maxUserId) {
            void linkBotUser(maxUserId, authResult.accessToken);
          }

          navigate('/home', { replace: true });
          return;
        }

        if (await resumeSession()) return;

        const esiaError = extractEsiaError(startParam);
        const isFreshError =
          Boolean(esiaError) &&
          Boolean(startParam) &&
          !isAuthPayloadConsumed(startParam!);

        if (exchangeFailed || isFreshError) {
          if (isFreshError) {
            markAuthPayloadConsumed(startParam!);
            trackAuthFailed('auth');
          }
          navigate('/error?kind=auth', { replace: true });
          return;
        }

        // Аноним начинает с Главной, как и в браузерной PWA: каталог и корзина
        // доступны без входа, он нужен только на оформлении
        navigate('/home', { replace: true });
      };

      void runMax();
      return;
    }

    // ── PWA-режим ──────────────────────────────────────────────────
    const params = new URLSearchParams(window.location.search);
    const error = params.get('error');
    const code = params.get('code');

    const runPwa = async () => {
      if (error) {
        trackAuthFailed('auth');
        if (await resumeSession()) return;
        navigate('/error?kind=auth', { replace: true });
        return;
      }

      if (code) {
        let authResult: AuthResult | null = null;
        let exchangeFailed = false;

        try {
          authResult = await initAuth(undefined, dispatch);
        } catch (err) {
          console.error('[initAuth]', err);
          exchangeFailed = true;
          trackAuthFailed(errorTypeOf(err));
        }

        if (authResult) {
          dispatch(
            setTokens({
              accessToken: authResult.accessToken,
              refreshToken: authResult.refreshToken,
            })
          );

          await registerDevice(dispatch).catch((err) =>
            console.error('[registerDevice]', err)
          );

          await mergeCarts(dispatch).catch((err) =>
            console.error('[mergeCarts]', err)
          );

          const regionFias = store.getState().region.regionFias;
          if (regionFias) {
            dispatch(
              api.endpoints.updateUser.initiate({
                address: { regionFiasId: regionFias },
              })
            ).catch((err) => console.error('[updateUser region]', err));
          }

          navigate('/home', { replace: true });
          return;
        }

        if (await resumeSession()) return;

        if (exchangeFailed) {
          navigate('/error?kind=auth', { replace: true });
          return;
        }
      }

      if (await resumeSession()) return;
      navigate('/home', { replace: true });
    };

    void runPwa();
  }, []);

  return null;
}
